import * as React from 'react'

import { Timestamp, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { zodResolver } from '@hookform/resolvers/zod'
import { endOfDay, format, startOfToday } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Grid, Text } from '@chakra-ui/react'

import {
  DatePicker,
  Input,
  InputGroup,
  Label,
  Loading,
  RadioButton,
} from '@enechain/ecloud-designsystem'
import { FaMagnifyingGlassRegular } from '@enechain/ecloud-icons'

import { FormSummary } from './FormSummary'
import { RoleCandidates } from './RoleCandidates'
import {
  ApiKeyFormFields,
  apiKeyFormSchema,
  parseExpiresAt,
} from './apiKeyFormSchema'

export type ApiKeyFormSubmitValues = {
  name: string
  roleNames: string[]
  expiresAt: Timestamp | undefined
  noExpiration: boolean
}

type Props = {
  defaultValues?: ApiKeyFormFields
  isSubmitting: boolean
  summary: {
    title: string
    submitLabel: string
    caution?: string
  }
  onSubmit: (values: ApiKeyFormSubmitValues) => void
}

export const ApiKeyForm: React.FC<Props> = ({
  defaultValues,
  isSubmitting,
  summary,
  onSubmit: onSubmitValues,
}) => {
  const { t } = useTranslation()
  const [query, setQuery] = React.useState('')
  const deferredQuery = React.useDeferredValue(query.trim())

  // DatePicker は非制御だと外側クリックで閉じないため、開閉を制御し
  // 入力欄とカレンダーを包む要素の外側クリックで閉じる
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false)
  const datePickerRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const closeOnOutsidePointerDown = (event: PointerEvent): void => {
      if (datePickerRef.current?.contains(event.target as Node) === true) {
        return
      }
      setIsDatePickerOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    return (): void => {
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
    }
  }, [])

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ApiKeyFormFields>({
    resolver: zodResolver(apiKeyFormSchema()),
    defaultValues: defaultValues ?? {
      name: '',
      roleNames: [],
      expirationType: '',
      expiresAt: '',
    },
  })
  const name = watch('name')
  const roleNames = watch('roleNames')
  const expirationType = watch('expirationType')
  const expiresAtValue = watch('expiresAt')

  const onToggleRole = (roleName: string): void => {
    const next = roleNames.includes(roleName)
      ? roleNames.filter((v) => v !== roleName)
      : [...roleNames, roleName]
    setValue('roleNames', next, { shouldValidate: true })
  }

  const onSubmit = handleSubmit((fields) => {
    const noExpiration = fields.expirationType === 'noExpiration'
    const expiresAt = noExpiration
      ? undefined
      : parseExpiresAt(fields.expiresAt)
    onSubmitValues({
      name: fields.name,
      roleNames: fields.roleNames,
      // 日付だけを選ばせるので、選んだ日いっぱいを有効とする
      expiresAt: expiresAt ? timestampFromDate(endOfDay(expiresAt)) : undefined,
      noExpiration,
    })
  })

  return (
    <Grid alignItems="start" gridGap="6" gridTemplateColumns="1fr 360px">
      <Flex flexDirection="column" gap="5">
        <Flex flexDirection="column" gap="1">
          <Label required>{t('pages.api_keys.label.name')}</Label>
          <Text textStyle="textSm" color="Text.midEmphasis">
            {t('pages.api_keys.form.name.hint')}
          </Text>
          <Input
            placeholder={t('pages.api_keys.form.name.placeholder')}
            {...register('name')}
            error={errors.name?.message}
          />
        </Flex>
        <Flex flexDirection="column" gap="4">
          <Flex alignItems="start" flexDirection="column" gap="4">
            <Flex flexDirection="column" gap="1">
              <Label required>
                {t('pages.api_keys.form.expiration.label')}
              </Label>
              <Text textStyle="textSm" color="Text.midEmphasis">
                {t('pages.api_keys.form.expiration.hint')}
              </Text>
              <Controller
                control={control}
                name="expirationType"
                render={({
                  field: { onChange, value },
                }): React.ReactElement => (
                  <RadioButton
                    // Design Systemで 縦並びを指定するとアイテムのテキスト調子次第でスタイルが崩れるので、一時的に横並びにする
                    // direction="column"
                    onValueChange={({ value }): void => {
                      if (value !== null) {
                        onChange(value)
                      }
                    }}
                    radios={[
                      {
                        label: t('pages.api_keys.form.expiration.date'),
                        value: 'date',
                      },
                      {
                        label: t('pages.api_keys.form.expiration.noExpiration'),
                        value: 'noExpiration',
                      },
                    ]}
                    value={value}
                  />
                )}
              />
              {errors.expirationType?.message !== undefined && (
                <Text textStyle="textSm" color="Text.negativeMid">
                  {errors.expirationType.message}
                </Text>
              )}
            </Flex>
            {expirationType === 'date' && (
              <Box ref={datePickerRef} width="50%">
                <DatePicker
                  anchorElement={
                    <Input
                      placeholder={t(
                        'pages.api_keys.form.expiresAt.placeholder',
                      )}
                      {...register('expiresAt')}
                      error={errors.expiresAt?.message}
                    />
                  }
                  value={parseExpiresAt(expiresAtValue)}
                  minDate={startOfToday()}
                  open={isDatePickerOpen}
                  onOpen={() => {
                    setIsDatePickerOpen(true)
                  }}
                  onClose={() => {
                    setIsDatePickerOpen(false)
                  }}
                  onClickOutside={() => {
                    setIsDatePickerOpen(false)
                  }}
                  onChange={(date) => {
                    setValue(
                      'expiresAt',
                      date ? format(date, 'yyyy/MM/dd') : '',
                      {
                        shouldValidate: true,
                      },
                    )
                    setIsDatePickerOpen(false)
                  }}
                />
              </Box>
            )}
          </Flex>
          <Flex flexDirection="column" gap="1">
            <Label required>{t('pages.api_keys.form.roles.label')}</Label>
            <Text textStyle="textSm" color="Text.midEmphasis">
              {t('pages.api_keys.form.roles.hint')}
            </Text>
            <InputGroup
              startElement={
                <FaMagnifyingGlassRegular color="Object.lowEmphasis" />
              }
            >
              <Input
                // DS の Input は InputGroup に認識されず左 padding が自動で付かないため明示する
                paddingLeft="10"
                placeholder={t('pages.api_keys.form.roles.searchPlaceholder')}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                }}
                width="500px"
              />
            </InputGroup>
            {errors.roleNames?.message !== undefined && (
              <Text textStyle="textSm" color="Text.negativeMid">
                {errors.roleNames.message}
              </Text>
            )}
          </Flex>
          <Flex
            flexDirection="column"
            gap="4"
            overflowY="scroll"
            height="560px"
            paddingRight="2"
          >
            <React.Suspense fallback={<Loading />}>
              <RoleCandidates
                query={deferredQuery}
                selectedRoleNames={roleNames}
                onToggle={onToggleRole}
              />
            </React.Suspense>
          </Flex>
        </Flex>
      </Flex>
      <FormSummary
        title={summary.title}
        submitLabel={summary.submitLabel}
        caution={summary.caution}
        name={name}
        selectedRoleNames={roleNames}
        isSubmitting={isSubmitting}
        onSubmit={() => {
          void onSubmit()
        }}
      />
    </Grid>
  )
}
